import React, { useEffect, useState } from "react";
import { Link, Message, Spin, Table, Tag, Typography } from "@arco-design/web-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import SummaryCard, { SummaryCardGroup } from "../Layout/SummaryCard";
import { FetchApi } from "../Api/AuthenticatedApi";
import { rangeLabel, viewerTimeZone } from "./reportRange";
import { spyReport } from "./spyReport";
import { plainClick } from "./plainClick";
import { reportLink } from "../utils/reportBack";

const { Text } = Typography;

// Its own section, loaded apart from signup activity: these numbers don't depend
// on the device filter, and a failure here leaves the rest of the report alone.
export default function SpySection({ range }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const [watches, setWatches] = useState(null);
  const [emails, setEmails] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/spy/watches/",
      (d) => setWatches(Array.isArray(d) ? d : []),
      () => navigate("/login"),
      () => setFailed(true));
  }, [getAccessTokenSilently, navigate]);

  useEffect(() => {
    setEmails(null);
    const q = new URLSearchParams({ from: range[0], to: range[1], tz: viewerTimeZone() });
    FetchApi(getAccessTokenSilently, "/api/reports/spy?" + q.toString(),
      (d) => setEmails((d && d.watches) || []),
      () => navigate("/login"),
      () => { setEmails([]); Message.error("Failed to load Email Spy numbers"); });
  }, [range, getAccessTokenSilently, navigate]);

  const report = spyReport(watches, emails);
  const n = (x) => (x || 0).toLocaleString();
  // Links out carry a way back here, with these filters.
  const toReportLink = (path) => reportLink(path, location.pathname + location.search);
  const open = (path) => plainClick(navigate, toReportLink(path));

  let body;
  if (failed) {
    body = <Text type="secondary">Couldn't load Email Spy.</Text>;
  } else if (watches && report.competitors === 0) {
    body = (
      <Text type="secondary">
        You're not watching any competitors yet. <Link href={toReportLink("/spy")} onClick={open("/spy")}>Go to Email Spy</Link>
      </Text>
    );
  } else {
    body = (
      <Spin loading={!watches || !emails} block>
        <SummaryCardGroup>
          <SummaryCard title="Competitors" value={n(report.competitors)} />
          <SummaryCard title="Working" value={n(report.working)} />
          <SummaryCard title="Need attention" value={n(report.needAttention)} />
          <SummaryCard title="Emails collected" value={n(report.emails)} />
          <SummaryCard title="Unread" value={n(report.unread)} />
        </SummaryCardGroup>
        <Table
          size="small"
          rowKey="id"
          style={{ marginTop: 12 }}
          pagination={report.rows.length > 10 ? { pageSize: 10, size: "small" } : false}
          data={report.rows}
          columns={[
            {
              title: "Competitor",
              dataIndex: "name",
              render: (name, row) => <Link href={toReportLink(`/spy/${row.id}`)} onClick={open(`/spy/${row.id}`)}>{name}</Link>,
            },
            {
              title: "Status",
              dataIndex: "status",
              render: (status, row) => <Tag color={row.ready ? "green" : "orange"}>{status}</Tag>,
            },
            { title: "Emails in these dates", dataIndex: "emails", render: n },
            { title: "Unread", dataIndex: "unread", render: n },
          ]}
        />
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
          An email that reaches two of your addresses counts twice. Unread is everything not yet opened, whatever the dates.
        </Text>
      </Spin>
    );
  }

  return <SummaryCard title="Email Spy" subTitle={rangeLabel(range)}>{body}</SummaryCard>;
}
